import type {
  Connector,
  ConnectorProviderType,
  CreateConnectorInput,
} from "../../types/connectors";
import { getTenantId } from "../../utils/jwtUtils";

const STORAGE_KEY_PREFIX = "sami_connectors";

function storageKey(tenantId: string): string {
  return `${STORAGE_KEY_PREFIX}:${tenantId}`;
}

function readAll(tenantId: string): Connector[] {
  try {
    const raw = localStorage.getItem(storageKey(tenantId));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as Connector[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeAll(tenantId: string, connectors: Connector[]): void {
  localStorage.setItem(storageKey(tenantId), JSON.stringify(connectors));
}

function resolveTenantId(tenantId?: string | null): string {
  return tenantId ?? getTenantId() ?? "default";
}

export const connectorService = {
  getConnectors: async (
    tenantId?: string | null,
  ): Promise<Connector[]> => {
    const tid = resolveTenantId(tenantId);
    return readAll(tid);
  },

  getConnectorsByProvider: async (
    providerType: ConnectorProviderType,
    tenantId?: string | null,
  ): Promise<Connector[]> => {
    const all = await connectorService.getConnectors(tenantId);
    return all.filter((c) => c.providerType === providerType);
  },

  createConnector: async (
    input: CreateConnectorInput,
    tenantId?: string | null,
  ): Promise<Connector> => {
    const tid = resolveTenantId(tenantId);
    const connectors = readAll(tid);
    const connector: Connector = {
      id: crypto.randomUUID(),
      ...input,
      createdAt: new Date().toISOString(),
    };
    writeAll(tid, [...connectors, connector]);
    return connector;
  },
};
